import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Manage from "@/v2-backoffice-client/components/manage"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { getCrossCustomerMembership } from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"
import UserTenantTypePicker from "@/v2-backoffice-client/user/tenant/type/UserTenantTypePicker"
import { useTenantUserAddMutation } from "@/v2-backoffice-client/tenant/user/add/tenant.user.add.mutation"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import { DEFAULT_TENANT_USER_TYPE_CODE } from "@/v2-backoffice-client/tenant/user/tenant.user.constants"

export type UserTenantManageSearchItemProps = {
  tenant: TenantFragment
  userId: number
  /** The user's owner, for the cross-customer guard. */
  user?: Pick<UserFragment, "Customer"> | null
  onAdded(roleCode: number): void
}

function UserTenantManageSearchItem(props: UserTenantManageSearchItemProps) {
  const { tenant, userId, user, onAdded } = props

  const [roleCode, setRoleCode] = useState(DEFAULT_TENANT_USER_TYPE_CODE)
  const [add, { error, loading }] = useTenantUserAddMutation({
    onCompleted() {
      onAdded(roleCode)
    },
  })

  errorToast(error, { title: "Failed to add user to team" })

  // A team under another customer than the user's owner: allowed (one
  // company sharing content with another's user), but rare on purpose, so
  // it takes a confirmation instead of a click.
  const crossing = getCrossCustomerMembership(user, tenant)

  return (
    <Manage.Item>
      <Manage.Item.Text
        primary={tenant.Name}
        secondary={tenant.CustomerName ?? undefined}
        warning={
          crossing
            ? `Belongs to ${crossing.teamCustomerName}, while the user is owned by ${crossing.ownerName}.`
            : undefined
        }
      />
      <UserTenantTypePicker value={roleCode} onChange={setRoleCode} size="xs" />
      <ConfirmButton
        size="xs"
        variant={crossing ? "warning" : "ghost"}
        isSquare
        iconSrc={crossing ? "triangle-warning" : "plus"}
        content={
          crossing
            ? "Add the user to a team under another customer"
            : "Add to team"
        }
        confirmContent={crossing ? "Add anyway?" : "Add?"}
        confirmVariant={crossing ? "warning" : undefined}
        isLoading={loading}
        onConfirm={() => {
          add({
            variables: {
              tenantId: tenant.Id,
              userId,
              userTenantTypeCode: roleCode,
            },
          })
        }}
      />
    </Manage.Item>
  )
}

export default UserTenantManageSearchItem
